import React, { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Clock, Users, User, Printer, FileDown, AlertTriangle, 
  Check, RefreshCw, Trash2, Calendar, BookOpen, Mail, Settings, Plus, Pencil,
  Sparkles, ArrowRight, Loader2, Zap, Brain, MoveRight, X, Eraser
} from 'lucide-react';
import { canEdit } from '@/lib/auth';

import ScheduleGrid from '@/components/schedule/ScheduleGrid';
import ScheduleEditor from '@/components/schedule/ScheduleEditor';

// Horários padrão (fallback)
const DEFAULT_TIME_SLOTS = [
  { id: '07:05', label: '07:05 - 07:50', start: '07:05', end: '07:50', turno: 'manha' },
  { id: '07:50', label: '07:50 - 08:35', start: '07:50', end: '08:35', turno: 'manha' },
  { id: '08:35', label: '08:35 - 09:20', start: '08:35', end: '09:20', turno: 'manha' },
  { id: '09:40', label: '09:40 - 10:25', start: '09:40', end: '10:25', turno: 'manha' },
  { id: '10:25', label: '10:25 - 11:10', start: '10:25', end: '11:10', turno: 'manha' },
  { id: '11:10', label: '11:10 - 11:55', start: '11:10', end: '11:55', turno: 'manha' },
  { id: '11:55', label: '11:55 - 12:40', start: '11:55', end: '12:40', turno: 'manha' },
  { id: '13:05', label: '13:05 - 13:50', start: '13:05', end: '13:50', turno: 'tarde' },
  { id: '13:50', label: '13:50 - 14:35', start: '13:50', end: '14:35', turno: 'tarde' },
  { id: '14:35', label: '14:35 - 15:20', start: '14:35', end: '15:20', turno: 'tarde' },
  { id: '15:20', label: '15:20 - 15:40', start: '15:20', end: '15:40', turno: 'tarde' },
  { id: '15:40', label: '15:40 - 16:25', start: '15:40', end: '16:25', turno: 'tarde' },
  { id: '16:25', label: '16:25 - 17:10', start: '16:25', end: '17:10', turno: 'tarde' },
  { id: '17:10', label: '17:10 - 17:55', start: '17:10', end: '17:55', turno: 'tarde' },
];

export default function ClassScheduleNew() {
  // Estados principais
  const [classGroups, setClassGroups] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
  
  // Estados do editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCell, setEditingCell] = useState({ day: '', timeSlot: '' });
  const [existingSchedule, setExistingSchedule] = useState(null);
  
  // Estados de conflito
  const [conflicts, setConflicts] = useState([]);
  const [conflictsDialogOpen, setConflictsDialogOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [conflictTab, setConflictTab] = useState('manual'); // 'manual' ou 'ai'
  const [movingSchedule, setMovingSchedule] = useState(null);
  const [moveDay, setMoveDay] = useState('');
  const [moveTimeSlot, setMoveTimeSlot] = useState('');
  
  // Estados para aulas fantasmas
  const [orphansDialogOpen, setOrphansDialogOpen] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  
  // Estados para visualização por professor
  const [viewMode, setViewMode] = useState('turma'); // 'turma' ou 'professor'
  const [allTeachers, setAllTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [teacherSchedules, setTeacherSchedules] = useState([]);
  const [selectedTurno, setSelectedTurno] = useState('todos');
  
  // Estados para gerenciamento de horários da turma
  const [useClassTimeSlots, setUseClassTimeSlots] = useState(false);
  const [newSlotStart, setNewSlotStart] = useState('');
  const [newSlotEnd, setNewSlotEnd] = useState('');
  const [newSlotTurno, setNewSlotTurno] = useState('manha');
  const [editingSlot, setEditingSlot] = useState(null);

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Carregar horários quando selecionar turma
  useEffect(() => {
    if (selectedClassId) {
      loadSchedules(selectedClassId);
      loadAssignments(selectedClassId);
    }
  }, [selectedClassId]);

  // Carregar horários do professor
  useEffect(() => {
    if (selectedTeacherId) {
      loadTeacherSchedules(selectedTeacherId);
    }
  }, [selectedTeacherId]);

  const loadInitialData = async () => {
    try {
      const [classGroupsRes, employeesRes, timeSlotsRes] = await Promise.all([
        api.get('/class-groups'),
        api.get('/employees?is_teacher=true'),
        api.get('/time-slots')
      ]);
      
      setClassGroups(classGroupsRes.data || []);
      setEmployees(employeesRes.data || []);
      
      // Carregar time slots do banco
      if (timeSlotsRes.data?.length > 0) {
        setTimeSlots(timeSlotsRes.data);
      }
      
      // Professores que têm horários
      const teachersWithSchedules = (employeesRes.data || [])
        .filter(e => e.is_teacher)
        .sort((a, b) => (a.full_name || a.name || '').localeCompare(b.full_name || b.name || ''));
      setAllTeachers(teachersWithSchedules);
      
      if (classGroupsRes.data?.length > 0) {
        setSelectedClassId(classGroupsRes.data[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Funções de gerenciamento de time slots
  const loadTimeSlots = async () => {
    try {
      const res = await api.get('/time-slots');
      if (res.data?.length > 0) {
        setTimeSlots(res.data);
      }
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
    }
  };

  const handleAddTimeSlot = async () => {
    if (!newSlotStart || !newSlotEnd) {
      toast.error('Preencha os horários de início e fim');
      return;
    }
    
    try {
      const newSlot = {
        id: newSlotStart,
        label: `${newSlotStart} - ${newSlotEnd}`,
        start: newSlotStart,
        end: newSlotEnd,
        turno: newSlotTurno
      };
      
      if (useClassTimeSlots && selectedClassId) {
        // Adicionar ao horário da turma
        const updatedSlots = [...timeSlots, newSlot].sort((a, b) => a.start.localeCompare(b.start));
        await api.post(`/time-slots/class/${selectedClassId}`, updatedSlots);
        setTimeSlots(updatedSlots);
      } else {
        await api.post('/time-slots', newSlot);
        loadTimeSlots();
      }
      
      toast.success('Horário adicionado');
      setNewSlotStart('');
      setNewSlotEnd('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar horário');
    }
  };

  const handleUpdateTimeSlot = async () => {
    if (!editingSlot) return;
    
    try {
      if (useClassTimeSlots && selectedClassId) {
        // Atualizar no horário da turma
        const updatedSlots = timeSlots.map(s => 
          s.id === editingSlot.id 
            ? {...editingSlot, label: `${editingSlot.start} - ${editingSlot.end}`}
            : s
        ).sort((a, b) => a.start.localeCompare(b.start));
        
        await api.post(`/time-slots/class/${selectedClassId}`, updatedSlots);
        setTimeSlots(updatedSlots);
      } else {
        await api.put(`/time-slots/${editingSlot.id}`, {
          start: editingSlot.start,
          end: editingSlot.end,
          turno: editingSlot.turno
        });
        loadTimeSlots();
      }
      
      toast.success('Horário atualizado');
      setEditingSlot(null);
      if (selectedClassId) {
        loadSchedules(selectedClassId);
      }
    } catch (error) {
      toast.error('Erro ao atualizar horário');
    }
  };

  // Resolver conflito removendo uma aula
  const handleResolveConflict = async (scheduleId) => {
    if (!confirm('Remover esta aula para resolver o conflito?')) return;
    
    try {
      await api.delete(`/class-schedules/conflicts/resolve/${scheduleId}`);
      toast.success('Conflito resolvido');
      loadConflicts();
      if (selectedClassId) {
        loadSchedules(selectedClassId);
      }
    } catch (error) {
      toast.error('Erro ao resolver conflito');
    }
  };

  // Limpar aulas órfãs/fantasmas
  const handleCleanupOrphans = async () => {
    setOrphansLoading(true);
    try {
      const res = await api.get('/class-schedules/orphans');
      setOrphans(res.data.orphans || []);
      setOrphansDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao buscar aulas fantasmas');
    } finally {
      setOrphansLoading(false);
    }
  };

  const handleDeleteOrphan = async (scheduleId) => {
    try {
      await api.delete(`/class-schedules/orphan/${scheduleId}`);
      setOrphans(prev => prev.filter(o => o.id !== scheduleId));
      toast.success('Aula removida');
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const handleDeleteAllOrphans = async () => {
    if (!confirm(`Excluir todas as ${orphans.length} aulas fantasmas?`)) return;
    try {
      await api.delete('/class-schedules/cleanup-orphans');
      setOrphans([]);
      toast.success('Todas as aulas fantasmas removidas');
      loadConflicts();
      if (selectedClassId) loadSchedules(selectedClassId);
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  // Solicitar sugestões de IA para resolver conflitos
  const handleAiSuggest = async () => {
    setAiLoading(true);
    setAiSuggestions([]);
    setAiSummary('');
    
    try {
      const res = await api.post('/class-schedules/conflicts/ai-resolve');
      if (res.data?.success) {
        setAiSuggestions(res.data.suggestions || []);
        setAiSummary(res.data.summary || '');
        setConflictTab('ai');
        toast.success('Sugestões de IA carregadas!');
      } else {
        toast.error(res.data?.error || 'Erro ao obter sugestões');
      }
    } catch (error) {
      toast.error('Erro ao conectar com IA');
    } finally {
      setAiLoading(false);
    }
  };

  // Mover aula para outro horário
  const handleMoveSchedule = async (scheduleId, newDay, newTimeSlot) => {
    try {
      await api.put(`/class-schedules/${scheduleId}/move`, null, {
        params: { new_day: newDay, new_time_slot: newTimeSlot }
      });
      toast.success('Aula movida com sucesso!');
      setMovingSchedule(null);
      loadConflicts();
      if (selectedClassId) {
        loadSchedules(selectedClassId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover aula');
    }
  };

  const handleDeleteTimeSlot = async (slotId) => {
    if (!confirm('Excluir este horário? Todas as aulas neste horário serão removidas.')) return;
    
    try {
      if (useClassTimeSlots && selectedClassId) {
        // Excluir do horário da turma
        const updatedSlots = timeSlots.filter(s => s.id !== slotId);
        await api.post(`/time-slots/class/${selectedClassId}`, updatedSlots);
        setTimeSlots(updatedSlots);
      } else {
        await api.delete(`/time-slots/${slotId}`);
        loadTimeSlots();
      }
      toast.success('Horário excluído');
      if (selectedClassId) {
        loadSchedules(selectedClassId);
      }
    } catch (error) {
      toast.error('Erro ao excluir horário');
    }
  };

  // Carregar horários específicos da turma
  const loadTimeSlotsForClass = async (classGroupId) => {
    try {
      const res = await api.get(`/time-slots/class/${classGroupId}`);
      if (res.data?.slots?.length > 0) {
        setTimeSlots(res.data.slots);
        setUseClassTimeSlots(res.data.source === 'custom');
      } else {
        // Se não tem slots específicos, carregar o padrão
        loadTimeSlots();
        setUseClassTimeSlots(false);
      }
    } catch (error) {
      console.error('Erro ao carregar horários da turma:', error);
      // Fallback para horários padrão
      loadTimeSlots();
      setUseClassTimeSlots(false);
    }
  };

  // Salvar horários da turma
  const handleSaveClassTimeSlots = async () => {
    if (!selectedClassId) return;
    
    try {
      await api.post(`/time-slots/class/${selectedClassId}`, timeSlots);
      toast.success('Horários salvos para esta turma');
      setUseClassTimeSlots(true);
    } catch (error) {
      toast.error('Erro ao salvar horários da turma');
    }
  };

  // Usar horários padrão
  const handleUseDefaultTimeSlots = async () => {
    if (!selectedClassId) return;
    
    try {
      await api.delete(`/time-slots/class/${selectedClassId}`);
      toast.success('Turma agora usa horários padrão');
      setUseClassTimeSlots(false);
      loadTimeSlots();
    } catch (error) {
      toast.error('Erro ao restaurar horários padrão');
    }
  };

  const loadSchedules = async (classGroupId) => {
    try {
      const res = await api.get(`/class-schedules?class_group_id=${classGroupId}`);
      setSchedules(res.data || []);
      loadConflicts();
      // Carregar horários específicos da turma
      loadTimeSlotsForClass(classGroupId);
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
    }
  };

  const loadAssignments = async (classGroupId) => {
    try {
      const res = await api.get(`/teacher-assignments?class_group_id=${classGroupId}`);
      setAssignments(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar vinculações:', error);
    }
  };

  const loadConflicts = async () => {
    try {
      const res = await api.get('/class-schedules/conflicts');
      setConflicts(res.data?.conflicts || []);
    } catch (error) {
      // Ignorar
    }
  };

  const loadTeacherSchedules = async (teacherId) => {
    try {
      const res = await api.get(`/class-schedules?employee_id=${teacherId}`);
      setTeacherSchedules(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar horários do professor');
    }
  };

  // Obter turma selecionada
  const selectedClass = classGroups.find(c => c.id === selectedClassId);
  const selectedClassName = selectedClass?.name || '';
  const selectedClassShift = selectedClass?.shift || 'Manhã';

  // Filtrar horários por turno
  const getFilteredTimeSlots = () => {
    if (selectedTurno === 'manha') {
      return timeSlots.filter(s => s.turno === 'manha');
    } else if (selectedTurno === 'tarde') {
      return timeSlots.filter(s => s.turno === 'tarde');
    }
    
    // Filtrar automaticamente pelo turno da turma
    if (selectedClassShift?.toLowerCase().includes('manhã') || selectedClassShift?.toLowerCase().includes('manha')) {
      return timeSlots.filter(s => s.turno === 'manha');
    } else if (selectedClassShift?.toLowerCase().includes('tarde')) {
      return timeSlots.filter(s => s.turno === 'tarde');
    }
    
    return timeSlots;
  };

  // Obter professores vinculados à turma com suas disciplinas
  const getLinkedTeachers = () => {
    const teacherMap = {};
    
    assignments.forEach(a => {
      if (!teacherMap[a.teacher_id]) {
        const emp = employees.find(e => e.id === a.teacher_id);
        teacherMap[a.teacher_id] = {
          id: a.teacher_id,
          name: a.teacher_name || (emp ? formatTeacherName(emp) : 'Professor'),
          subjects: []
        };
      }
      
      // Usar subjects array do backend ou subject_name individual
      if (a.subjects && a.subjects.length > 0) {
        a.subjects.forEach(sub => {
          if (!teacherMap[a.teacher_id].subjects.find(s => s.id === sub.id)) {
            teacherMap[a.teacher_id].subjects.push({
              id: sub.id,
              name: sub.name,
              assignment_id: a.id
            });
          }
        });
      } else if (a.subject_name && a.subject_id) {
        if (!teacherMap[a.teacher_id].subjects.find(s => s.id === a.subject_id)) {
          teacherMap[a.teacher_id].subjects.push({
            id: a.subject_id,
            name: a.subject_name,
            assignment_id: a.id
          });
        }
      }
    });
    
    return Object.values(teacherMap);
  };

  const formatTeacherName = (employee) => {
    if (!employee) return '';
    const fullName = employee.full_name || employee.name || '';
    const parts = fullName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[parts.length - 1]}`;
    }
    return fullName;
  };

  // Verificar conflito de professor
  const checkConflict = async (employeeId, day, timeSlot, excludeScheduleId = null) => {
    if (!employeeId) return null;
    
    try {
      // Buscar todas as aulas desse professor nesse dia
      const res = await api.get(`/class-schedules?employee_id=${employeeId}&day=${day}`);
      
      // Turma atual
      const currentClass = classGroups.find(g => g.id === selectedClassId);
      const currentTurno = currentClass?.shift || currentClass?.turno;
      
      // Filtrar conflitos reais
      const conflicting = (res.data || []).filter(s => {
        if (s.id === excludeScheduleId) return false;
        if (s.class_group_id === selectedClassId) return false;
        
        // Verificar se o time_slot é o mesmo
        if (s.time_slot !== timeSlot) return false;
        
        // Verificar se é o MESMO turno
        const otherClass = classGroups.find(g => g.id === s.class_group_id);
        const otherTurno = otherClass?.shift || otherClass?.turno;
        
        // Se ambos têm turno definido e são diferentes, NÃO é conflito
        if (currentTurno && otherTurno && currentTurno !== otherTurno) {
          return false;
        }
        
        return true;
      });
      
      if (conflicting.length > 0) {
        const turma = classGroups.find(g => g.id === conflicting[0].class_group_id);
        return { turma: turma?.name || 'Outra turma' };
      }
    } catch (error) {
      // Ignorar
    }
    return null;
  };

  // Handler para clique na célula
  const handleCellClick = (day, timeSlot) => {
    if (!canEdit()) return;
    
    const existing = schedules.find(s => s.day === day && s.time_slot === timeSlot);
    setExistingSchedule(existing || null);
    setEditingCell({ day, timeSlot });
    setEditorOpen(true);
  };

  // Salvar horário
  const handleSaveSchedule = async (data) => {
    try {
      if (existingSchedule) {
        // Atualizar
        await api.put(`/class-schedules/${existingSchedule.id}`, {
          ...data,
          class_group_id: selectedClassId
        });
      } else {
        // Criar
        await api.post('/class-schedules', {
          ...data,
          class_group_id: selectedClassId
        });
      }
      loadSchedules(selectedClassId);
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  // Excluir horário
  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/class-schedules/${scheduleId}`);
      loadSchedules(selectedClassId);
    } catch (error) {
      throw new Error('Erro ao excluir');
    }
  };

  // Obter label do time slot
  const getTimeSlotLabel = (timeSlotId) => {
    const slot = timeSlots.find(s => s.id === timeSlotId);
    return slot?.label || timeSlotId;
  };

  // Imprimir quadro
  const handlePrint = () => {
    // Criar área de impressão com o quadro de horários
    const selectedClass = classGroups.find(c => c.id === selectedClassId);
    const className = selectedClass?.name || 'Horário';
    
    const days = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    // Filtrar slots pelo turno da turma
    const turno = selectedClass?.shift?.toLowerCase() || '';
    let slotsToUse = timeSlots;
    if (turno.includes('manhã') || turno.includes('manha')) {
      slotsToUse = timeSlots.filter(s => s.turno === 'manha');
    } else if (turno.includes('tarde')) {
      slotsToUse = timeSlots.filter(s => s.turno === 'tarde');
    }
    
    // Gerar HTML da tabela
    let tableHtml = `
      <div class="print-area" id="print-schedule">
        <style>
          .print-header { text-align: center; margin-bottom: 20px; }
          .print-header h1 { font-size: 18pt; margin: 0; }
          .print-header p { color: #666; margin: 5px 0; }
          .schedule-table { width: 100%; border-collapse: collapse; font-size: 10pt; }
          .schedule-table th, .schedule-table td { border: 1px solid #333; padding: 6px 8px; text-align: center; }
          .schedule-table th { background: #4B0082; color: white; }
          .schedule-table .time-col { width: 100px; background: #f5f5f5; font-weight: bold; }
          .schedule-table .subject { font-weight: bold; }
          .schedule-table .teacher { font-size: 9pt; color: #666; }
        </style>
        <div class="print-header">
          <h1>Quadro de Horários - ${className}</h1>
          <p>Ano Letivo 2026</p>
        </div>
        <table class="schedule-table">
          <thead>
            <tr>
              <th>Horário</th>
              ${days.map(d => `<th>${d}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
    `;
    
    // Gerar linhas para cada slot
    slotsToUse.forEach(slot => {
      tableHtml += `<tr>
        <td class="time-col">${slot.label || `${slot.start} - ${slot.end}`}</td>`;
      
      dayKeys.forEach(dayKey => {
        const schedule = schedules.find(s => s.day === dayKey && s.time_slot === slot.id);
        if (schedule) {
          tableHtml += `<td>
            <div class="subject">${schedule.subject_name || '-'}</div>
            <div class="teacher">${schedule.employee_name || ''}</div>
          </td>`;
        } else {
          tableHtml += `<td>-</td>`;
        }
      });
      
      tableHtml += `</tr>`;
    });
    
    tableHtml += `
          </tbody>
        </table>
        <p style="margin-top: 20px; font-size: 9pt; color: #999; text-align: center;">
          Impresso em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}
        </p>
      </div>
    `;
    
    // Criar uma janela de impressão
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Horário - ${className}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${tableHtml}
        <div class="no-print" style="margin-top: 20px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 30px; font-size: 14px; cursor: pointer;">
            Imprimir
          </button>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Estatísticas do quadro
  const scheduleStats = {
    total: schedules.length,
    porDia: {
      monday: schedules.filter(s => s.day === 'monday').length,
      tuesday: schedules.filter(s => s.day === 'tuesday').length,
      wednesday: schedules.filter(s => s.day === 'wednesday').length,
      thursday: schedules.filter(s => s.day === 'thursday').length,
      friday: schedules.filter(s => s.day === 'friday').length,
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header - responsivo */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 sm:w-7 sm:h-7 text-indigo-600" />
            Quadro de Horários
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">
            Gerencie os horários de aula das turmas
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Botão de Conflitos - sempre visível */}
          <Button 
            variant={conflicts.length > 0 ? "destructive" : "outline"}
            size="sm"
            onClick={() => {
              loadConflicts();
              setConflictsDialogOpen(true);
            }}
            className="flex items-center gap-1"
          >
            {conflicts.length > 0 ? (
              <>
                <AlertTriangle className="w-4 h-4" />
                <span className="hidden sm:inline">{conflicts.length} Conflito(s)</span>
                <span className="sm:hidden">{conflicts.length}</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span className="hidden sm:inline">Ver Conflitos</span>
              </>
            )}
          </Button>
          
          {/* Botão Limpar Aulas Fantasmas */}
          {canEdit() && (
            <Button 
              variant="outline"
              size="sm"
              onClick={handleCleanupOrphans}
              disabled={orphansLoading}
              className="flex items-center gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
              title="Ver e remover aulas órfãs/fantasmas"
            >
              <Eraser className="w-4 h-4" />
              <span className="hidden sm:inline">Limpar Fantasmas</span>
            </Button>
          )}
        </div>
      </div>

      {/* Dialog de Conflitos - MODERNO COM IA */}
      <Dialog open={conflictsDialogOpen} onOpenChange={setConflictsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white p-4">
            <DialogTitle className="flex items-center gap-2 text-lg font-bold">
              <AlertTriangle className="w-5 h-5" />
              Central de Conflitos
              {conflicts.length > 0 && (
                <Badge className="bg-white/20 text-white ml-2">
                  {conflicts.length} conflito(s)
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-red-100 mt-1">
              Resolva conflitos de horário manualmente ou com ajuda da IA
            </DialogDescription>
          </div>

          {/* Abas */}
          <Tabs value={conflictTab} onValueChange={setConflictTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="border-b px-4 bg-slate-50">
              <TabsList className="bg-transparent h-12">
                <TabsTrigger value="manual" className="data-[state=active]:bg-white gap-2">
                  <Pencil className="w-4 h-4" />
                  Resolver Manual
                </TabsTrigger>
                <TabsTrigger value="ai" className="data-[state=active]:bg-white gap-2">
                  <Sparkles className="w-4 h-4" />
                  Resolver com IA
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Aba Manual */}
            <TabsContent value="manual" className="flex-1 overflow-y-auto p-4 m-0">
              {conflicts.length === 0 ? (
                <div className="text-center py-12">
                  <Check className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-xl font-semibold text-green-700">Nenhum Conflito!</h3>
                  <p className="text-muted-foreground mt-2">Todos os horários estão corretos.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {conflicts.map((conflict, idx) => (
                    <Card key={idx} className="border-red-200 bg-red-50/50">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <CardTitle className="text-base">{conflict.employee_name}</CardTitle>
                              <CardDescription className="text-red-600">
                                {conflict.day_label} às {conflict.time_slot}
                              </CardDescription>
                            </div>
                          </div>
                          <Badge variant="destructive">
                            {conflict.schedules?.length || 0} aulas conflitantes
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-2">
                        <div className="space-y-2">
                          {conflict.schedules?.map((schedule, sIdx) => (
                            <div key={sIdx} className="flex items-center justify-between bg-white p-3 rounded-lg border shadow-sm">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-bold">
                                  {sIdx + 1}
                                </div>
                                <div>
                                  <p className="font-medium">{schedule.class_name}</p>
                                  <p className="text-sm text-muted-foreground">{schedule.subject_name}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setMovingSchedule(schedule);
                                    setMoveDay('');
                                    setMoveTimeSlot('');
                                  }}
                                >
                                  <MoveRight className="w-4 h-4 mr-1" />
                                  Mover
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleResolveConflict(schedule.schedule_id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Remover
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Aba IA */}
            <TabsContent value="ai" className="flex-1 overflow-y-auto p-4 m-0">
              {!aiSuggestions.length && !aiLoading ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Brain className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">Resolver com Inteligência Artificial</h3>
                  <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                    A IA analisará os conflitos e sugerirá as melhores soluções para reorganizar os horários automaticamente.
                  </p>
                  <Button 
                    className="mt-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    onClick={handleAiSuggest}
                    disabled={aiLoading || conflicts.length === 0}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Gerar Sugestões com IA
                  </Button>
                  {conflicts.length === 0 && (
                    <p className="text-sm text-green-600 mt-3">
                      ✓ Não há conflitos para resolver
                    </p>
                  )}
                </div>
              ) : aiLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-purple-600" />
                  <h3 className="text-lg font-semibold">Analisando conflitos...</h3>
                  <p className="text-muted-foreground mt-2">A IA está processando as melhores soluções</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Resumo da IA */}
                  {aiSummary && (
                    <Alert className="bg-purple-50 border-purple-200">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <AlertDescription className="text-purple-800">
                        <strong>Resumo da IA:</strong> {aiSummary}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Sugestões */}
                  {aiSuggestions.map((suggestion, idx) => (
                    <Card key={idx} className="border-purple-200 bg-purple-50/30">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-purple-900">{suggestion.conflito}</p>
                            <div className="mt-2 flex items-center gap-2 text-sm">
                              <Badge variant="outline" className="bg-white">
                                {suggestion.aula_original}
                              </Badge>
                              <ArrowRight className="w-4 h-4 text-purple-600" />
                              <Badge className="bg-purple-600">
                                {suggestion.acao === 'mover' ? suggestion.novo_horario : 'Remover'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              {suggestion.justificativa}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => toast.info('Funcionalidade de aplicar sugestão em desenvolvimento')}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            Aplicar
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Botão para regenerar */}
                  <div className="text-center pt-4">
                    <Button variant="outline" onClick={handleAiSuggest} disabled={aiLoading}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerar Sugestões
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="border-t p-4 bg-slate-50 flex justify-between">
            <Button variant="outline" onClick={loadConflicts}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            <Button variant="outline" onClick={() => setConflictsDialogOpen(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para mover aula */}
      <Dialog open={!!movingSchedule} onOpenChange={() => setMovingSchedule(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MoveRight className="w-5 h-5 text-blue-600" />
              Mover Aula
            </DialogTitle>
            <DialogDescription>
              Escolha o novo dia e horário para esta aula
            </DialogDescription>
          </DialogHeader>
          
          {movingSchedule && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-medium">{movingSchedule.class_name}</p>
                <p className="text-sm text-muted-foreground">{movingSchedule.subject_name}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Novo Dia</Label>
                  <Select value={moveDay} onValueChange={setMoveDay}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Segunda</SelectItem>
                      <SelectItem value="tuesday">Terça</SelectItem>
                      <SelectItem value="wednesday">Quarta</SelectItem>
                      <SelectItem value="thursday">Quinta</SelectItem>
                      <SelectItem value="friday">Sexta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Novo Horário</Label>
                  <Select value={moveTimeSlot} onValueChange={setMoveTimeSlot}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(slot => (
                        <SelectItem key={slot.id} value={slot.id}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovingSchedule(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleMoveSchedule(movingSchedule?.schedule_id, moveDay, moveTimeSlot)}
              disabled={!moveDay || !moveTimeSlot}
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmar Mudança
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Aulas Fantasmas */}
      <Dialog open={orphansDialogOpen} onOpenChange={setOrphansDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="w-5 h-5 text-orange-600" />
              Aulas Fantasmas ({orphans.length})
            </DialogTitle>
            <DialogDescription>
              Aulas que referenciam turmas ou professores que não existem mais
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-2">
            {orphans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Check className="w-12 h-12 mx-auto text-green-500 mb-2" />
                Nenhuma aula fantasma encontrada
              </div>
            ) : (
              orphans.map(orphan => (
                <div key={orphan.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {orphan.day} - {orphan.time_slot}
                    </div>
                    <div className="text-xs text-gray-600">
                      Turma: {orphan.class_name || 'N/A'} | Prof: {orphan.employee_name || 'N/A'}
                    </div>
                    <div className="text-xs text-red-600">
                      {orphan.orphan_reasons?.join(', ')}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteOrphan(orphan.id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOrphansDialogOpen(false)}>
              Fechar
            </Button>
            {orphans.length > 0 && (
              <Button variant="destructive" onClick={handleDeleteAllOrphans}>
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir Todas ({orphans.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs de Visualização - responsivo */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-xs sm:max-w-md grid-cols-2">
          <TabsTrigger value="turma" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <Users className="w-3 h-3 sm:w-4 sm:h-4" />
            Por Turma
          </TabsTrigger>
          <TabsTrigger value="professor" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
            <User className="w-3 h-3 sm:w-4 sm:h-4" />
            Por Professor
          </TabsTrigger>
        </TabsList>

        {/* TAB: Por Turma */}
        <TabsContent value="turma" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
          {/* Seletor de Turma - responsivo */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
            <Select value={selectedClassId} onValueChange={setSelectedClassId}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Selecione a turma" />
              </SelectTrigger>
              <SelectContent>
                {classGroups.map(group => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name} {group.shift ? `(${group.shift})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrint} size="sm" className="flex-1 sm:flex-none">
                <Printer className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Imprimir</span>
              </Button>
              
              <Button variant="outline" onClick={() => loadSchedules(selectedClassId)} size="sm" className="flex-1 sm:flex-none">
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Atualizar</span>
              </Button>
            </div>
          </div>

          {/* Estatísticas - responsivo */}
          {selectedClassId && (
            <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
              {/* Indicador de Horário Padrão/Personalizado */}
              <Badge 
                variant={useClassTimeSlots ? 'default' : 'secondary'} 
                className={`text-[10px] sm:text-xs ${
                  useClassTimeSlots 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {useClassTimeSlots ? '⚙️ Horário Personalizado' : '📋 Horário Padrão'}
              </Badge>
              <Badge variant="secondary" className="text-[10px] sm:text-xs">
                {scheduleStats.total} aulas
              </Badge>
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] sm:text-xs">
                Seg: {scheduleStats.porDia.monday}
              </Badge>
              <Badge variant="outline" className="border-rose-300 bg-rose-50 text-[10px] sm:text-xs">
                Ter: {scheduleStats.porDia.tuesday}
              </Badge>
              <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-[10px] sm:text-xs">
                Qua: {scheduleStats.porDia.wednesday}
              </Badge>
              <Badge variant="outline" className="border-orange-300 bg-orange-50 text-[10px] sm:text-xs">
                Qui: {scheduleStats.porDia.thursday}
              </Badge>
              <Badge variant="outline" className="border-cyan-300 bg-cyan-50 text-[10px] sm:text-xs">
                Sex: {scheduleStats.porDia.friday}
              </Badge>
            </div>
          )}

          {/* Grid de Horários - com scroll horizontal em mobile */}
          {selectedClassId && (
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg p-3 sm:p-6">
                <CardTitle className="text-center text-sm sm:text-lg">
                  {selectedClassName}
                </CardTitle>
                {selectedClassShift && (
                  <CardDescription className="text-center text-indigo-100 text-xs sm:text-sm">
                    Turno: {selectedClassShift}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <ScheduleGrid
                  timeSlots={getFilteredTimeSlots()}
                  schedules={schedules}
                  onCellClick={handleCellClick}
                  canEdit={canEdit()}
                  conflicts={conflicts}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Por Professor */}
        <TabsContent value="professor" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Selecione o professor" />
              </SelectTrigger>
              <SelectContent>
                {allTeachers.map(teacher => (
                  <SelectItem key={teacher.id} value={teacher.id}>
                    {teacher.full_name || teacher.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedTurno} onValueChange={setSelectedTurno}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Turno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="manha">Manhã</SelectItem>
                <SelectItem value="tarde">Tarde</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedTeacherId && (
            <Card>
              <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-lg">
                <CardTitle className="text-center text-lg">
                  Horários de {allTeachers.find(t => t.id === selectedTeacherId)?.full_name || 'Professor'}
                </CardTitle>
                <CardDescription className="text-center text-purple-100">
                  {teacherSchedules.length} aulas cadastradas
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScheduleGrid
                  timeSlots={selectedTurno === 'todos' ? timeSlots : timeSlots.filter(s => s.turno === selectedTurno)}
                  schedules={teacherSchedules}
                  onCellClick={() => {}} // Não permitir edição nesta view
                  canEdit={false}
                  conflicts={[]}
                  viewMode="teacher"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Editor de Horário */}
      <ScheduleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        day={editingCell.day}
        timeSlot={editingCell.timeSlot}
        timeSlotLabel={getTimeSlotLabel(editingCell.timeSlot)}
        turmaName={selectedClassName}
        linkedTeachers={getLinkedTeachers()}
        existingSchedule={existingSchedule}
        onSave={handleSaveSchedule}
        onDelete={handleDeleteSchedule}
        checkConflict={checkConflict}
      />
    </div>
  );
}
